import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AdminHeader from '../components/layout/admin_header';
import AdminSidebar from '../components/layout/admin_sidebar';
// import AdminFooter from './AdminFooter';
import LoadingSpinner from '../components/elements/LoadingSpinner';
import ErrorBoundary from '../components/error/ErrorBoundary';
// import NotificationToast from '../common/NotificationToast';
import { useAuth } from '../context/AuthContext';
// import { useTheme } from '../context/ThemeContext';
import '../assets/css/layout/admin_layout.css';

const LayoutAdmin = () => {
    // eslint-disable-next-line no-unused-vars
    const { user: _user, loading } = useAuth();
    // const { theme } = useTheme();
    const location = useLocation();
    
    // Layout states
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        return localStorage.getItem('sidebarCollapsed') === 'true';
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [pageLoading, setPageLoading] = useState(false);
    const [, setNotifications] = useState([]);
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    
    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            
            // Auto-collapse sidebar on mobile
            if (mobile && !sidebarCollapsed) {
                setSidebarCollapsed(true);
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [sidebarCollapsed]);
    
    // Handle online/offline status
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);
    
    // Save sidebar state to localStorage
    useEffect(() => {
        localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
    }, [sidebarCollapsed]);
    
    // Handle route changes
    useEffect(() => {
        setPageLoading(true);
        const timer = setTimeout(() => setPageLoading(false), 300);
        
        // Close mobile sidebar on route change
        if (isMobile) {
            setSidebarCollapsed(true);
        }
        
        // Scroll to top on route change
        window.scrollTo(0, 0);
        
        return () => clearTimeout(timer);
    }, [location.pathname, isMobile]);
    
    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyboard = (e) => {
            // Toggle sidebar with Ctrl/Cmd + B
            if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
                e.preventDefault();
                setSidebarCollapsed(!sidebarCollapsed);
            }
            
            // Quick search with Ctrl/Cmd + K
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                // Focus search in header
                const searchInput = document.querySelector('.search-trigger');
                if (searchInput) {
                    searchInput.click();
                }
            }
            
            // Escape key to close mobile sidebar
            if (e.key === 'Escape' && isMobile && !sidebarCollapsed) {
                setSidebarCollapsed(true);
            }
        };
        
        document.addEventListener('keydown', handleKeyboard);
        return () => document.removeEventListener('keydown', handleKeyboard);
    }, [sidebarCollapsed, isMobile]);
    
    // Add notification
    const addNotification = (notification) => {
        const id = Date.now().toString();
        const newNotification = { ...notification, id };
        setNotifications(prev => [...prev, newNotification]);
        
        // Auto-remove after 5 seconds
        setTimeout(() => {
            removeNotification(id);
        }, 5000);
    };
    
    // Remove notification
    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
    };
    
    // Get page title for document title
    const getPageTitle = () => {
        const path = location.pathname;
        const titles = {
            '/admin': 'Dashboard',
            '/admin/farmers': 'Farmer Management',
            '/admin/cases': 'Case Management',
            '/admin/farms': 'Farm Management',
            '/admin/doctors': 'Doctor Management',
            '/admin/analytics': 'Analytics',
            '/admin/settings': 'Settings',
            '/admin/reports': 'Reports'
        };
        return titles[path] || 'Admin Panel';
    };
    
    // Update document title
    useEffect(() => {
        document.title = `${getPageTitle()} - E-AquaHealth Admin`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
    
    // Show loading spinner during auth loading
    if (loading) {
        return (
            <div className="admin-layout-loading">
                <LoadingSpinner size="large" />
                <p>Loading admin panel...</p>
            </div>
        );
    }
    
    return (
        <ErrorBoundary>
            <div 
                className={`admin-layout 
                    ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${isMobile ? 'mobile' : ''}`}
                // data-theme={theme}
            >
                {/* Offline Banner */}
                {!isOnline && (
                    <div className="offline-banner">
                        <div className="offline-content">
                            <span className="offline-icon">⚠️</span>
                            <span>You are currently offline. Some features may be limited.</span>
                        </div>
                    </div>
                )}
                
                {/* Admin Sidebar */}
                <AdminSidebar 
                    isCollapsed={sidebarCollapsed} 
                    setIsCollapsed={setSidebarCollapsed}
                />
                
                {/* Main Content Area */}
                <div className="admin-main">
                    {/* Admin Header */}
                    <AdminHeader 
                        sidebarCollapsed={sidebarCollapsed}
                        setSidebarCollapsed={setSidebarCollapsed}
                        addNotification={addNotification}
                    />
                    
                    {/* Page Content */}
                    <main className="admin-content">
                        {/* Content Header */}
                        <div className="content-header">
                            <div className="content-header-main">
                                {/* <BreadcrumbNavigation /> */}
                                <div className="page-meta">
                                    <h1 className="page-title">{getPageTitle()}</h1>
                                    <p className="page-description">
                                        {getPageDescription()}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Quick Actions */}
                            {/* <div className="content-header-actions">
                                <QuickActions currentPath={location.pathname} />
                            </div> */}
                        </div>
                        
                        {/* Page Loading Overlay */}
                        {pageLoading && (
                            <div className="page-loading-overlay">
                                <LoadingSpinner />
                            </div>
                        )}
                        
                        {/* Main Content */}
                        <div className="content-body">
                            <ErrorBoundary>
                                <Outlet context={{ addNotification }} />
                            </ErrorBoundary>
                        </div>
                        
                        {/* Content Footer */}
                        <div className="content-footer">
                            <div className="footer-links">
                                <a href="/admin/help" className="footer-link">Help Center</a>
                                <a href="/admin/support" className="footer-link">Support</a>
                                <a href="/admin/docs" className="footer-link">Documentation</a>
                            </div>
                            <div className="footer-info">
                                <span>Last updated: {new Date().toLocaleString()}</span>
                            </div>
                        </div>
                    </main>
                </div>
                
                {/* Notification Toasts */}
                {/* <div className="notification-container">
                    {notifications.map(notification => (
                        <NotificationToast
                            key={notification.id}
                            notification={notification}
                            onClose={() => removeNotification(notification.id)}
                        />
                    ))}
                </div> */}
                
                {/* Keyboard Shortcuts Help */}
                <div className="keyboard-shortcuts-hint">
                    <span>Press Ctrl+B to toggle sidebar</span>
                </div>
                
                {/* Development Tools (only in dev mode) */}
                {process.env.NODE_ENV === 'development' && (
                    <div className="dev-tools">
                        <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                            Toggle Sidebar
                        </button>
                        <button onClick={() => addNotification({
                            type: 'success',
                            title: 'Test Notification',
                            message: 'This is a test notification'
                        })}>
                            Test Notification
                        </button>
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
    
    // Helper function to get page description
    function getPageDescription() {
        const path = location.pathname;
        const descriptions = {
            '/admin': 'Overview of your platform performance and key metrics',
            '/admin/farmers': 'Manage platform users, roles, and permissions',
            '/admin/cases': 'Monitor and manage aquaculture health cases',
            '/admin/farms': 'Oversee registered farms and their operations',
            '/admin/doctors': 'Manage veterinarian accounts and specializations',
            '/admin/analytics': 'Analyze platform usage and performance metrics',
            '/admin/settings': 'Configure platform settings and preferences',
            '/admin/reports': 'Generate and view system reports'
        };
        return descriptions[path] || 'Manage your E-AquaHealth platform';
    }
};

export default LayoutAdmin;